import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model, OnInit, Signal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import { TableType } from '@soposopo/sopo-models';
import { filter, map, startWith } from "rxjs";
import { FeatureService } from "src/app/core/firebase/feature.service";
import { ClubStateDispatcher } from "src/app/core/state/club/club.dispatcher";
import { ClubComponentId } from "src/app/core/state/club/club.state";
import { HomeLobbyStateDispatcher } from "src/app/core/state/lobby/homeLobby.dispatcher";
import { HomeLobbyComponentId } from "src/app/core/state/lobby/homeLobby.state";
import { LobbyService } from "src/app/lobby/pages/lobby-home/lobby.service";
import { SopoTransitionService } from "../../services/sopo-transition/sopo-transition.service";

export type LobbyMenuSelectorTypes = 'gold' | 'sweeps' | 'clubs' | 'quickplay';
export type SocialMenuSelectorTypes = 'videos' | 'news' | 'staking' | 'picks';

@Component({
    selector: 'app-tab-selector',
    templateUrl: './tab-selector.component.html',
    styleUrls: ['./tab-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TabSelectorComponent implements OnInit {
    forGameTypes = input<boolean>(false);
    forLeaderboard = input<boolean>(false);
    isDesktop = signal<boolean>(false);
    activeGameTypesMenu = model<TableType | undefined>(undefined);
    activeMenu = linkedSignal<LobbyMenuSelectorTypes | undefined>(() =>
        this.lobbyService.activeMenu() ?? undefined
    );
    activeSocialMenu = linkedSignal<SocialMenuSelectorTypes>(() =>
        this.lobbyService.activeSocialMenu() ?? 'videos'
    );
    activatedComponent = toSignal(
        this.homeLobbyDispatcher.getActivatedComponent().pipe(map(c => c.componentId)),
        { initialValue: 'lobby-home' as HomeLobbyComponentId }
    );
    clubComponent = toSignal(
        this.clubDispatch.getActivatedComponent().pipe((map(c => c.componentId))),
        { initialValue: 'games' as ClubComponentId }
    )
    currentUrl = toSignal(
        this.router.events.pipe(filter(e => e instanceof NavigationEnd),
            map((e: NavigationEnd) => e.urlAfterRedirects),
            startWith(this.router.url)
        ),
        { initialValue: this.router.url }
    );

    constructor(
        private router: Router,
        private lobbyService: LobbyService,
        private featureService: FeatureService,
        private navCtrl: NavController,
        private homeLobbyDispatcher: HomeLobbyStateDispatcher,
        private clubDispatch: ClubStateDispatcher,
        private sopoTransitionService: SopoTransitionService,
    ) {}

    ngOnInit(): void {
        this.isDesktop.set(this.featureService.isDesktop);
        if (this.forGameTypes()) {
            const gameTab = this.router.getCurrentNavigation()?.extras?.queryParams?.['gameTab'];
            if (gameTab) this.activeGameTypesMenu.set(gameTab);
        }
    }

    isLobbyHome = computed(() => {
        return this.currentUrl().includes("/lobby/home") &&
            !this.currentUrl().includes("/lobby/home/sweepstakes") &&
            this.activatedComponent() !== "club-list";
    })

    isSocial = computed(() => {
        return this.currentUrl().includes("/social");
    })

    selectors: Signal<LobbyMenuSelectorTypes[] | SocialMenuSelectorTypes[] | TableType[]> = computed(() => {
        if (this.forGameTypes()) return [TableType.SNG, TableType.MTT, TableType.RING];
        if (this.forLeaderboard()) return ['gold', 'sweeps'];
        if (this.isSocial()) {
            return ['videos', 'news', 'staking'];
        }
        if (!this.isDesktop()) return ['clubs', 'quickplay'];
        return ['gold', 'sweeps', 'clubs', 'quickplay'];
    })

    async onSelectMenu(selectedMenu: LobbyMenuSelectorTypes | SocialMenuSelectorTypes | TableType) {
        if (!this.forGameTypes() && this.activeMenu() === selectedMenu) return;

        if(this.isSocial()) {
            this.activeSocialMenu.update(() => selectedMenu as SocialMenuSelectorTypes);
            this.lobbyService.setActiveSocialMenu(selectedMenu as SocialMenuSelectorTypes);
            return;
        }

        if(!this.forGameTypes()) {
            if(this.activatedComponent() === 'club-list') {
                this.homeLobbyDispatcher.activateComponent('lobby-home');
            }
            this.lobbyService.setActiveLobbyMenu(selectedMenu as LobbyMenuSelectorTypes);
            if (!this.isLobbyHome()) {
                await this.sopoTransitionService.openTransition();
                this.navCtrl.navigateRoot('/lobby/home');
            }
        } else {
            this.activeGameTypesMenu.update(() => selectedMenu as TableType);
        };
    }

    getBackButtonText = computed(() => {
        let backText = 'Back to ';
        if(this.activeMenu() === 'clubs') {
            if(this.currentUrl().includes('/lobby/home') && this.activatedComponent() === 'club-list') {
                return backText + 'Club Home';
            } else if (this.clubComponent() !== 'games' || this.currentUrl().includes('create-table') || this.currentUrl().includes('tournament-registration')) {
                return backText + 'Club Lobby';
            } else {
                return backText + 'My Clubs';
            }
        } else if(this.currentUrl().includes('/tournament')) {
            return backText + 'Lobby';
        } else if(this.activeMenu() === 'sweeps') {
            return backText + 'Sweepstakes Home';
        } else if(this.activeMenu() === 'gold') {
            return backText + 'Gold Home';
        } else if(this.activeMenu() === 'quickplay') {
            return backText + 'Quickplay Home';
        }
        return backText + 'Lobby';
    });

    async navigateBack() {
        if(this.activeMenu() === 'clubs') {
            if(this.currentUrl().includes('/lobby/home') && this.activatedComponent() === 'club-list') {
                this.homeLobbyDispatcher.activateComponent('lobby-home');
                return;
            } else if (this.currentUrl().includes('create-table')) {
                this.navCtrl.navigateBack(this.currentUrl().replace('/create-table', ''));
                return;
            } else if (this.currentUrl().includes('tournament-registration')) {
                const clubId = this.currentUrl().split('/')[2];
                this.navCtrl.navigateBack(`/club-lobby/${clubId}`);
                return;
            } else if (this.clubComponent() !== 'games') {
                this.clubDispatch.activateComponent('games');
                return;
            }
            await this.sopoTransitionService.openTransition();
            this.navCtrl.navigateRoot('/lobby/home', {queryParams: {openClubList: true}});
            return;
        } else if (this.activeMenu() === 'sweeps') {
            if(this.currentUrl().includes('/tournament-queue')) {
                this.navCtrl.navigateBack('/lobby/home/sweepstakes', {queryParams: {tabs: 'sweepstakes', noTransition: true, gameTab: 'SNG'}});
                return;
            } else if(this.currentUrl().includes('/tournament-registration')) {
                this.navCtrl.navigateBack('/lobby/home/sweepstakes', {queryParams: {tabs: 'sweepstakes', noTransition: true, gameTab: 'MTT'}});
                return;
            }
        } else if (this.activeMenu() === 'gold') {
            if(this.currentUrl().includes('/tournament-queue')) {
                this.navCtrl.navigateBack('/lobby/home/sweepstakes', {queryParams: {tabs: 'freeplay', noTransition: true, gameTab: 'SNG'}});
                return;
            } else if(this.currentUrl().includes('/tournament-registration')) {
                this.navCtrl.navigateBack('/lobby/home/sweepstakes', {queryParams: {tabs: 'freeplay', noTransition: true, gameTab: 'MTT'}});
                return;
            }
        }

        await this.sopoTransitionService.openTransition();
        this.navCtrl.navigateRoot('/lobby/home');
    }
}
